
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const initialized = new WeakSet();

function money(cents, currency){
  try{
    return new Intl.NumberFormat(undefined,{style:'currency',currency:currency || 'EUR'}).format((Number(cents)||0)/100);
  }catch(e){
    return `€${((Number(cents)||0)/100).toFixed(2)}`;
  }
}

async function initCC(root){
  if(!root || initialized.has(root)) return;
  root.dataset.ccJsStarted = 'true';
  initialized.add(root);

  const viewer=root.querySelector('[data-cc-viewer]');
  const loading=root.querySelector('[data-cc-loading]');
  const message=root.querySelector('[data-cc-message]');

  const modelUrls={
    silver:{
      stand:{
        black:root.dataset.inoxStandBlack || '',
        yellow:root.dataset.inoxStandYellow || '',
        pink:root.dataset.inoxStandPink || '',
        turquoise:root.dataset.inoxStandTurquoise || '',
        red:root.dataset.inoxStandRed || '',
        orange:root.dataset.inoxStandOrange || '',
        white:root.dataset.inoxStandWhite || ''
      },
      tray:root.dataset.inoxTray || ''
    },
    black:{
      stand:{
        black:root.dataset.obsidianStandBlack || '',
        yellow:root.dataset.obsidianStandYellow || '',
        pink:root.dataset.obsidianStandPink || '',
        turquoise:root.dataset.obsidianStandTurquoise || '',
        red:root.dataset.obsidianStandRed || '',
        orange:root.dataset.obsidianStandOrange || '',
        white:root.dataset.obsidianStandWhite || ''
      },
      tray:root.dataset.obsidianTray || ''
    }
  };

  const ORING_NAMES=['black','yellow','pink','turquoise','red','orange','white'];
  const modelAvailable={
    silver:!!modelUrls.silver.tray && ORING_NAMES.every(c=>!!modelUrls.silver.stand[c]),
    black:!!modelUrls.black.tray && ORING_NAMES.every(c=>!!modelUrls.black.stand[c])
  };

  if(!modelAvailable.silver && !modelAvailable.black){
    loading.textContent='3D model set incomplete.';
    message.textContent='In Theme Editor check the 3D model status box. Each Stand product needs six 3D models with the required O-ring alt text.';
    message.classList.add('is-error');
    return;
  }
  const variants={
    silver:{
      stand: root.dataset.standSilverVariant || '',
      tray: root.dataset.traySilverVariant || ''
    },
    black:{
      stand: root.dataset.standBlackVariant || '',
      tray: root.dataset.trayBlackVariant || ''
    }
  };
  const prices={
    silver:{
      stand: Number(root.dataset.standSilverPrice || 0),
      tray: Number(root.dataset.traySilverPrice || 0)
    },
    black:{
      stand: Number(root.dataset.standBlackPrice || 0),
      tray: Number(root.dataset.trayBlackPrice || 0)
    }
  };
  const currency=root.dataset.currency || 'EUR';
  const walnutBaseUrl=root.dataset.walnutBase || '';
  const walnutRoughnessUrl=root.dataset.walnutRoughness || '';
  const walnutBumpUrl=root.dataset.walnutBump || '';

  const scene=new THREE.Scene();
  const camera=new THREE.PerspectiveCamera(32,1,.1,5000);
  camera.position.set(420,245,470);

  const renderer=new THREE.WebGLRenderer({antialias:true,alpha:false,powerPreference:'high-performance'});
  renderer.setPixelRatio(Math.min(devicePixelRatio,2.25));
  renderer.shadowMap.enabled=true;
  renderer.shadowMap.type=THREE.PCFSoftShadowMap;
  renderer.outputColorSpace=THREE.SRGBColorSpace;
  renderer.toneMapping=THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure=0.70;
  viewer.appendChild(renderer.domElement);

  // Real environment reflections make the silver finish read as metal rather than black.
  const pmrem=new THREE.PMREMGenerator(renderer);
  const envTexture=pmrem.fromScene(new RoomEnvironment(renderer),.14).texture;
  scene.environment=envTexture;
  pmrem.dispose();

  const controls=new OrbitControls(camera,renderer.domElement);
  controls.enableDamping=true;
  controls.target.set(40,36,0);
  controls.minDistance=150;
  controls.maxDistance=1800;

  scene.add(new THREE.HemisphereLight(0xf5f6f7,0x111418,0.42));
  const key=new THREE.DirectionalLight(0xffffff,0.88);
  key.position.set(-320,430,360);key.castShadow=true;key.shadow.mapSize.set(4096,4096);scene.add(key);
  const soft=new THREE.DirectionalLight(0xd8e1ea,0.28);soft.position.set(370,220,-260);scene.add(soft);
  const rim=new THREE.DirectionalLight(0xffffff,0.38);rim.position.set(0,300,-430);scene.add(rim);
  // Product-photography softboxes. These affect lighting only;
  // product materials remain the baked GLB materials.
  const softboxLeft=new THREE.RectAreaLight(0xffffff,2.4,420,620);
  softboxLeft.position.set(-430,330,280);
  softboxLeft.lookAt(40,45,0);
  scene.add(softboxLeft);

  const softboxRight=new THREE.RectAreaLight(0xe8edf2,1.55,300,500);
  softboxRight.position.set(470,210,160);
  softboxRight.lookAt(40,35,0);
  scene.add(softboxRight);

  const stripLight=new THREE.RectAreaLight(0xffffff,1.15,620,120);
  stripLight.position.set(40,430,-280);
  stripLight.lookAt(40,45,0);
  scene.add(stripLight);


  const woodMat=new THREE.MeshStandardMaterial({color:0x2b1c14,roughness:.78,metalness:0});
  const tabletop=new THREE.Mesh(new THREE.BoxGeometry(1650,34,720),woodMat);
  tabletop.position.set(220,-17,0);tabletop.receiveShadow=true;scene.add(tabletop);
  // Photoreal dark walnut tabletop. Product models/materials remain untouched.
  if(walnutBaseUrl && walnutRoughnessUrl && walnutBumpUrl){
    const texLoader=new THREE.TextureLoader();
    const [walnutBase,walnutRough,walnutBump]=await Promise.all([
      texLoader.loadAsync(walnutBaseUrl),
      texLoader.loadAsync(walnutRoughnessUrl),
      texLoader.loadAsync(walnutBumpUrl)
    ]);

    walnutBase.colorSpace=THREE.SRGBColorSpace;

    [walnutBase,walnutRough,walnutBump].forEach(tex=>{
      tex.wrapS=THREE.RepeatWrapping;
      tex.wrapT=THREE.RepeatWrapping;
      // Long boards / grain run left-to-right across the configurator surface.
      tex.repeat.set(1.45,1.05);
      tex.anisotropy=Math.min(8,renderer.capabilities.getMaxAnisotropy());
    });

    tabletop.material=new THREE.MeshPhysicalMaterial({
      name:'CC_PHOTO_WALNUT',
      map:walnutBase,
      roughnessMap:walnutRough,
      bumpMap:walnutBump,
      bumpScale:.16,
      color:0xffffff,
      metalness:0,
      roughness:.70,
      clearcoat:.04,
      clearcoatRoughness:.62,
      envMapIntensity:.38
    });
    tabletop.material.needsUpdate=true;
  }

  const shadowMat=new THREE.ShadowMaterial({color:0x000000,opacity:.22});
  const shadowPlane=new THREE.Mesh(new THREE.PlaneGeometry(2200,1300),shadowMat);
  shadowPlane.rotation.x=-Math.PI/2;
  shadowPlane.position.set(220,-34,0);
  shadowPlane.receiveShadow=true;
  scene.add(shadowPlane);


  // Restrained lifestyle scene.
  const lifestyle=new THREE.Group();scene.add(lifestyle);
  const wall=new THREE.Mesh(new THREE.BoxGeometry(1900,900,18),new THREE.MeshStandardMaterial({color:0xb7a996,roughness:.95}));
  wall.position.set(220,-275,-530);lifestyle.add(wall);
  const floor=new THREE.Mesh(new THREE.BoxGeometry(1900,18,1200),new THREE.MeshStandardMaterial({color:0x71553c,roughness:.9}));
  floor.position.set(220,-720,30);lifestyle.add(floor);
  const credenza=new THREE.Mesh(new THREE.BoxGeometry(1250,610,560),new THREE.MeshStandardMaterial({color:0x2d2019,roughness:.63}));
  credenza.position.set(220,-322,0);lifestyle.add(credenza);
  const plinth=new THREE.Mesh(new THREE.BoxGeometry(1020,34,480),new THREE.MeshStandardMaterial({color:0x161616,roughness:.72}));
  plinth.position.set(220,-644,0);lifestyle.add(plinth);
  const dark=new THREE.MeshStandardMaterial({color:0x171717,metalness:.25,roughness:.43});
  const frame=new THREE.Mesh(new THREE.BoxGeometry(340,245,12),dark);frame.position.set(250,260,-517);lifestyle.add(frame);
  const art=new THREE.Mesh(new THREE.BoxGeometry(306,211,5),new THREE.MeshStandardMaterial({color:0xc9bdad,roughness:.9}));
  art.position.set(250,260,-509);lifestyle.add(art);
  const stem=new THREE.Mesh(new THREE.CylinderGeometry(7,9,165,22),dark);stem.position.set(-400,83,-300);lifestyle.add(stem);
  const lampBase=new THREE.Mesh(new THREE.CylinderGeometry(48,52,10,28),dark);lampBase.position.set(-400,5,-300);lifestyle.add(lampBase);
  const shade=new THREE.Mesh(new THREE.CylinderGeometry(62,92,85,30,1,true),new THREE.MeshStandardMaterial({color:0x181818,roughness:.48,side:THREE.DoubleSide}));
  shade.position.set(-400,205,-300);lifestyle.add(shade);
  const lamp=new THREE.PointLight(0xffc985,1.35,650);lamp.position.set(-400,175,-300);lifestyle.add(lamp);
  const pot=new THREE.Mesh(new THREE.CylinderGeometry(38,45,75,28),new THREE.MeshStandardMaterial({color:0x8a735d,roughness:.8}));
  pot.position.set(650,38,-290);lifestyle.add(pot);
  for(let i=0;i<7;i++){
    const leaf=new THREE.Mesh(new THREE.BoxGeometry(12,95,4),new THREE.MeshStandardMaterial({color:0x23412e,roughness:.78}));
    leaf.position.set(650+(i-3)*10,110+Math.abs(i-3)*5,-290);leaf.rotation.z=(i-3)*.18;lifestyle.add(leaf);
  }

  const loader=new GLTFLoader();
  const assets={};
  async function loadAsset(k,url){
    const gltf=await loader.loadAsync(url);
    const object=gltf.scene;
    object.traverse(o=>{if(o.isMesh){o.castShadow=true;o.receiveShadow=true}});
    assets[k]=object;
  }

  try{
    loading.textContent='Loading Stand and Tray models…';
    const loads=[];
    for(const f of ['silver','black']){
      if(!modelAvailable[f]) continue;
      for(const c of ORING_NAMES){
        loads.push(loadAsset(`${f}_stand_${c}`,modelUrls[f].stand[c]));
      }
      loads.push(loadAsset(`${f}_tray`,modelUrls[f].tray));
    }
    await Promise.all(loads);
    loading.classList.add('is-hidden');
  }catch(err){
    console.error(err);
    loading.textContent='Could not load the 3D product models.';
    message.textContent='Check that both GLB assets were uploaded to the theme.';
    message.classList.add('is-error');
    return;
  }

  let sequence=['stand'];
  let finish=modelAvailable.silver ? 'silver' : 'black';
  let oringColor='black';
  let instances=[];
  const INTERLOCK_PITCH=80.0;
  function cloneProduct(type){
    const assetKey = type==='stand'
      ? `${finish}_stand_${oringColor}`
      : `${finish}_tray`;
    const source=assets[assetKey];
    return source ? source.clone(true) : null;
  }

  function clear3D(){
    instances.forEach(o=>scene.remove(o));
    instances=[];
  }
  function rebuild(refit=false){
    clear3D();

    // Always centre the active configuration around the viewer origin.
    // V34 used a fixed -120 X offset, which left the single default stand
    // displaced on first load and could make it miss the initial camera view.
    const count=sequence.length;
    const startX=count > 0 ? -((count-1)*INTERLOCK_PITCH)/2 : 0;

    sequence.forEach((type,i)=>{
      const obj=cloneProduct(type);
      if(!obj) return;
      obj.visible=true;
      obj.position.set(startX+i*INTERLOCK_PITCH,.25,0);
      obj.updateMatrixWorld(true);
      scene.add(obj);
      instances.push(obj);
    });
    scene.updateMatrixWorld(true);
    renderSummary();
    if(refit)fitView();
  }

  function renderSummary(){
    const el=root.querySelector('[data-cc-summary]');
    el.innerHTML='';
    const standQty=sequence.filter(x=>x==='stand').length;
    const trayQty=sequence.filter(x=>x==='tray').length;
    root.querySelector('[data-cc-stand-count]').textContent=standQty;
    root.querySelector('[data-cc-tray-count]').textContent=trayQty;
    sequence.forEach((type,i)=>{
      const row=document.createElement('div');
      row.className='cc-native__row';
      row.innerHTML=`<span>${i+1}. ${type==='stand'?'Puzzle Watch Stand':'Vanity Tray'}</span><button type="button" class="cc-native__minus" data-index="${i}" aria-label="Remove item">−</button>`;
      el.appendChild(row);
    });
    el.querySelectorAll('.cc-native__minus').forEach(btn=>{
      btn.addEventListener('click',()=>{
        sequence.splice(Number(btn.dataset.index),1);
        rebuild(false);
      });
    });
    if(!sequence.length){
      const n=document.createElement('div');
      n.className='cc-native__note';
      n.textContent='No products selected.';
      el.appendChild(n);
    }
    const activePrices=prices[finish] || prices.silver;
    root.querySelector('[data-cc-total]').textContent=
      money(standQty*activePrices.stand + trayQty*activePrices.tray, currency);
  }

  function selectORing(name){
    oringColor=name;
    root.querySelectorAll('[data-cc-oring]').forEach(b=>{
      b.classList.toggle('is-active',b.dataset.ccOring===name);
    });
    rebuild(false);
    message.textContent=`O-rings: ${name.charAt(0).toUpperCase()+name.slice(1)}.`;
    message.classList.remove('is-error');
  }

  function selectFinish(name){
    if(!modelAvailable[name]){
      message.textContent=`${name==='silver'?'INOX':'Obsidian'} 3D model pair is missing from the selected Shopify products.`;
      message.classList.add('is-error');
      return;
    }

    finish=name;

    root.querySelectorAll('[data-cc-finish]').forEach(button=>{
      button.classList.toggle('is-active',button.dataset.ccFinish===name);
    });

    root.querySelectorAll('[data-cc-preview]').forEach(button=>{
      button.classList.toggle('is-active',button.dataset.ccPreview===name);
    });

    rebuild(false);
    message.textContent=`${name==='silver'?'INOX':'Obsidian'} finish model loaded.`;
    message.classList.remove('is-error');
  }

  function useStudioScene(){
    lifestyle.visible=false;
    scene.background = new THREE.Color(0x050607);
    root.dataset.ccScene='studio';
  }

  // Quantity listeners are registered exactly once.
  root.querySelector('[data-cc-stand-plus]').addEventListener('click',()=>{
    sequence.push('stand');
    rebuild(false);
  });

  root.querySelector('[data-cc-stand-minus]').addEventListener('click',()=>{
    const i=sequence.lastIndexOf('stand');
    if(i>=0){
      sequence.splice(i,1);
      rebuild(false);
    }
  });

  root.querySelector('[data-cc-tray-plus]').addEventListener('click',()=>{
    sequence.push('tray');
    rebuild(false);
  });

  root.querySelector('[data-cc-tray-minus]').addEventListener('click',()=>{
    const i=sequence.lastIndexOf('tray');
    if(i>=0){
      sequence.splice(i,1);
      rebuild(false);
    }
  });

  root.querySelectorAll('[data-cc-finish]').forEach(b=>b.addEventListener('click',()=>selectFinish(b.dataset.ccFinish)));
  root.querySelectorAll('[data-cc-preview]').forEach(b=>b.addEventListener('click',()=>selectFinish(b.dataset.ccPreview)));
  root.querySelectorAll('[data-cc-oring]').forEach(b=>b.addEventListener('click',()=>selectORing(b.dataset.ccOring)));
  root.querySelector('[data-cc-clear]').addEventListener('click',()=>{sequence=[];rebuild(false)});

  root.querySelector('[data-cc-cart]').addEventListener('click',async()=>{
    message.classList.remove('is-error','is-success');
    if(!sequence.length){
      message.textContent='Add at least one product before adding to cart.';
      message.classList.add('is-error');
      return;
    }
    const standQty=sequence.filter(x=>x==='stand').length;
    const trayQty=sequence.filter(x=>x==='tray').length;
    const activeVariants=variants[finish] || variants.silver;
    if((standQty && !activeVariants.stand) || (trayQty && !activeVariants.tray)){
      message.textContent=
        `Select the ${finish==='silver'?'INOX':'Obsidian'} Stand and Tray products in this section’s Theme Editor settings.`;
      message.classList.add('is-error');
      return;
    }

    const cartButton=root.querySelector('[data-cc-cart]');
    cartButton.disabled=true;
    cartButton.textContent='Adding…';

    const configId=(crypto && crypto.randomUUID) ? crypto.randomUUID() : `cc-${Date.now()}`;
    const sequenceLabel=sequence.map(x=>x==='stand'?'Puzzle Watch Stand':'Vanity Tray').join(' → ');
    const properties={
      'Finish': finish==='silver'?'INOX':'Obsidian',
      'O-ring colour': oringColor.charAt(0).toUpperCase()+oringColor.slice(1),
      'Configuration': sequenceLabel,
      '_Chrome Calibre configuration ID': configId
    };

    const items=[];
    if(standQty) items.push({id:Number(activeVariants.stand),quantity:standQty,properties});
    if(trayQty) items.push({id:Number(activeVariants.tray),quantity:trayQty,properties});

    const shopRoot=(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try{
      const response=await fetch(`${shopRoot}cart/add.js`,{
        method:'POST',
        headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({items})
      });
      if(!response.ok) throw new Error(await response.text());
      message.textContent='Configuration added to cart.';
      message.classList.add('is-success');
      document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));
    }catch(err){
      console.error(err);
      message.textContent='The configuration could not be added to cart. Check product availability and theme cart settings.';
      message.classList.add('is-error');
    }finally{
      cartButton.disabled=false;
      cartButton.textContent='Add configuration to cart';
    }
  });

  function setCamera(pos,target){
    camera.position.set(...pos);controls.target.set(...target);controls.update();
  }
  root.querySelector('[data-cc-top-view]').addEventListener('click',()=>setCamera([40,650,.01],[40,0,0]));
  root.querySelector('[data-cc-front-view]').addEventListener('click',()=>setCamera([40,120,650],[40,35,0]));
  root.querySelector('[data-cc-right-view]').addEventListener('click',()=>setCamera([650,120,0],[40,35,0]));
  root.querySelectorAll('[data-cc-reset]').forEach(b=>b.addEventListener('click',fitView));

  function fitView(){
    if(!instances.length){
      camera.position.set(420,245,470);controls.target.set(40,36,0);controls.update();return;
    }
    const box=new THREE.Box3();
    instances.forEach(o=>box.expandByObject(o));
    const center=box.getCenter(new THREE.Vector3());
    const size=box.getSize(new THREE.Vector3());
    const span=Math.max(size.x,size.z,size.y*1.7);
    const dist=Math.max(330,span/Math.tan(THREE.MathUtils.degToRad(camera.fov/2))*.92);
    camera.position.set(center.x+dist*.70,center.y+dist*.34,center.z+dist*.80);
    controls.target.set(center.x,Math.max(28,center.y*.5),center.z);controls.update();
  }

  function resize(){
    const w=viewer.clientWidth;
    const h=viewer.clientHeight;
    renderer.setSize(w,h,false);
    camera.aspect=w/h;
    camera.updateProjectionMatrix();
  }
  const observer=new ResizeObserver(resize);
  observer.observe(viewer);
  resize();

  let running=true;
  function animate(){
    if(!running)return;
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene,camera);
  }
  animate();

  root._ccDestroy=()=>{
    running=false;
    observer.disconnect();
    renderer.dispose();
    envTexture.dispose();
  };

  useStudioScene();

  // Set the initial UI state before the first scene build. This avoids
  // rebuilding the default stand multiple times during startup and ensures
  // the first Inox stand is visible immediately when the configurator loads.
  root.querySelectorAll('[data-cc-finish]').forEach(button=>{
    button.classList.toggle('is-active',button.dataset.ccFinish===finish);
  });
  root.querySelectorAll('[data-cc-preview]').forEach(button=>{
    button.classList.toggle('is-active',button.dataset.ccPreview===finish);
  });
  root.querySelectorAll('[data-cc-oring]').forEach(button=>{
    button.classList.toggle('is-active',button.dataset.ccOring==='black');
  });

  // V43: initialization now completes normally because useStudioScene() no longer
  // throws when scene.background is null. Build the default stand once, then frame it.
  rebuild(false);
  resize();
  scene.updateMatrixWorld(true);
  instances.forEach(object=>object.updateMatrixWorld(true));
  fitView();
  controls.update();
  renderer.render(scene,camera);

  message.textContent='1 × Puzzle Watch Stand ready to configure.';
  message.classList.remove('is-error');
}

function initAll(scope=document){
  scope.querySelectorAll?.('.cc-native').forEach(initCC);
}

if(document.readyState==='loading'){
  document.addEventListener('DOMContentLoaded',()=>initAll());
}else{
  initAll();
}
document.addEventListener('shopify:section:load',event=>initAll(event.target));
document.addEventListener('shopify:section:unload',event=>{
  event.target.querySelectorAll?.('.cc-native').forEach(root=>root._ccDestroy?.());
});
